import { ChatDetailsResponse } from '@choco-admin/types';
import { getInitials } from '@choco-admin/utils';
import { Avatar, Chip, Button } from '@heroui/react';
import React, { useState } from 'react';
import { FiUsers, FiMail, FiPhone, FiX, FiPlus } from 'react-icons/fi';

import { useNotification } from '../../../../../../../contexts/NotificationContext';
import { useChatUserOperations } from '../../../../../hooks';
import { useChatPageContext } from '../../../contexts';

import { AddBuyerUserModal } from './AddBuyerUserModal';

interface BuyerUsersSectionProps {
  chatData: ChatDetailsResponse;
  chatId: string;
}

export const BuyerUsersSection: React.FC<BuyerUsersSectionProps> = ({
  chatData,
  chatId,
}) => {
  const { chatData: contextChatData, updateChatData } = useChatPageContext();
  const currentChatData = contextChatData || chatData;
  const buyerUsers = currentChatData.users.filter((user) => !user.supplier);
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const { addNotification } = useNotification();
  const { removeUsers, isLoading } = useChatUserOperations();

  const handleRemoveUser = async (userId: string) => {
    setRemovingUserId(userId);
    try {
      const users = await removeUsers([
        { chatId, userId, userType: 'buyer', operation: 'remove' },
      ]);

      // Write the server-true user list from the mutation response so the row
      // disappears immediately.
      updateChatData((prevData) =>
        prevData ? { ...prevData, users } : prevData
      );

      addNotification({
        title: 'User Removed',
        message: 'User has been successfully removed from the chat.',
        type: 'success',
      });
    } catch (error) {
      addNotification({
        title: 'Remove Failed',
        message:
          error instanceof Error
            ? error.message
            : 'Failed to remove user from chat.',
        type: 'error',
      });
    } finally {
      setRemovingUserId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FiUsers className="w-4 h-4 text-default-400" />
          <div>
            <p className="text-xs font-medium text-default-500 tracking-wide mb-0.5">
              Buyer Users
            </p>
            <p className="text-sm text-default-500">
              {buyerUsers.length} user{buyerUsers.length !== 1 ? 's' : ''} with
              access to this chat
            </p>
          </div>
        </div>
        <Button
          size="sm"
          variant="flat"
          color="primary"
          startContent={<FiPlus className="w-4 h-4" />}
          onPress={() => setIsAddModalOpen(true)}
        >
          Add User
        </Button>
      </div>

      {buyerUsers.length > 0 ? (
        <div className="space-y-3">
          {buyerUsers.map((user) => (
            <div
              key={user.id}
              className="flex items-center justify-between p-4 rounded-lg bg-content2 border border-divider"
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <Avatar
                  name={getInitials(user.name || 'U')}
                  getInitials={(value) => value}
                  size="md"
                  className="text-small shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-foreground truncate">
                    {user.name}
                  </p>
                  <div className="space-y-0.5 text-sm text-default-600 mt-1">
                    {user.email && (
                      <div
                        className="flex items-center gap-1.5 min-w-0"
                        title={user.email}
                      >
                        <FiMail className="w-3.5 h-3.5 shrink-0 text-default-500" />
                        <span className="truncate">{user.email}</span>
                      </div>
                    )}
                    {user.phone && (
                      <div className="flex items-center gap-1.5">
                        <FiPhone className="w-3.5 h-3.5 shrink-0 text-default-500" />
                        <span>{user.phone}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <div className="flex gap-2">
                  {user.orderPreferences?.whatsApp && (
                    <Chip size="sm" variant="flat" color="success">
                      WhatsApp
                    </Chip>
                  )}
                  {user.orderPreferences?.sms && (
                    <Chip size="sm" variant="flat" color="primary">
                      SMS
                    </Chip>
                  )}
                  {user.orderPreferences?.fax && (
                    <Chip size="sm" variant="flat" color="secondary">
                      Fax
                    </Chip>
                  )}
                </div>
                <Button
                  isIconOnly
                  size="sm"
                  color="danger"
                  variant="light"
                  isLoading={removingUserId === user.id}
                  isDisabled={isLoading}
                  onPress={() => handleRemoveUser(user.id)}
                  aria-label={`Remove ${user.name} from chat`}
                >
                  <FiX className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="p-4 bg-content2 rounded-lg border border-divider">
          <p className="text-default-500 italic">No buyer users assigned</p>
        </div>
      )}

      {/* Add User Modal */}
      <AddBuyerUserModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        chatId={chatId}
        buyerId={currentChatData.buyerEntity?.id || ''}
      />
    </div>
  );
};
